import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import { showError, showSuccess } from '../utils/alerts';
import RegionComunaSelects from '../components/common/RegionComunaSelects';
import { REGIMENES_TRIBUTARIOS } from '../services/regimenesTributarios';
import '../styles/components/EmpresaForm.css';
import '../styles/pages/CompanySettingsPage.css';

const CompanySettingsPage = ({ token, id_company, setCompany }) => {
  const [form, setForm] = useState({
    name: '',
    rut: '',
    address: '',
    rep_legal: '',
    rut_representante: '',
    comuna: '',
    region: '',
    email: '',
    phone: '',
    area: '',
    empresario_individual: false,
    regimen_tributario: '',
    tasa_ppm: '',
    clave_sii: '',
    conciliacion_bancaria: '',
    recursos_humanos: ''
  });
  const [loading, setLoading] = useState(true);
  const [phoneError, setPhoneError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/company/companies/${id_company}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setForm({ ...response.data, email: response.data.email || '', phone: response.data.phone || '', clave_sii: '' });
      } catch (error) {
        console.error('Error al obtener datos de la empresa:', error);
        showError('No se pudieron cargar los datos de la empresa');
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [id_company, token]);

  const validatePhone = (value) => {
    if (!value) return ''; // opcional: vacío es válido
    if (!value.startsWith('56')) return 'El teléfono debe comenzar con 56 (ej: 56912345678)';
    if (value.length !== 11) return 'El teléfono debe tener exactamente 11 dígitos (ej: 56912345678)';
    if (!/^\d+$/.test(value)) return 'El teléfono debe contener solo números';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setPhoneError(validatePhone(value));
    }
    const cambios = { [name]: value };
    // Empresario individual: el nombre de la empresa es el del representante legal
    if (form.empresario_individual && name === 'rep_legal') cambios.name = value;
    setForm({ ...form, ...cambios });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validatePhone(form.phone);
    if (error) {
      setPhoneError(error);
      return;
    }
    try {
      const response = await axios.put(`${API_BASE_URL}/api/company/companies/${id_company}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompany({
        id_company: response.data.id_company,
        name: response.data.name,
        conciliacion_bancaria: response.data.conciliacion_bancaria,
        recursos_humanos: response.data.recursos_humanos
      });
      showSuccess('Datos de la empresa actualizados');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error al actualizar empresa:', error);
      showError(error.response?.data?.message || 'Error al actualizar empresa');
    }
  };

  if (loading) {
    return <div className="company-settings-container"><p>Cargando datos de la empresa...</p></div>;
  }

  return (
    <div className="company-settings-container">
      <h1>Configuración de la Empresa</h1>
      <form className="company-settings-form" onSubmit={handleSubmit}>
        <div className="empresa-campo">
          <label htmlFor="empresa-name" className="empresa-campo__etiqueta">Nombre de la empresa</label>
          <input
            id="empresa-name"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Ej: Comercial Los Andes SpA"
            readOnly={form.empresario_individual}
            title={form.empresario_individual ? 'Es el nombre del representante legal' : undefined}
            required
          />
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-rut" className="empresa-campo__etiqueta">RUT de la empresa</label>
          <input id="empresa-rut" name="rut" value={form.rut} disabled title="El RUT de la empresa no se puede modificar" />
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-address" className="empresa-campo__etiqueta">Dirección</label>
          <input id="empresa-address" name="address" value={form.address} onChange={handleChange} placeholder="Ej: Av. Providencia 1234, of. 56" required />
        </div>
        <RegionComunaSelects
          region={form.region}
          comuna={form.comuna}
          onChange={(cambios) => setForm({ ...form, ...cambios })}
        />
        <div className="empresa-campo">
          <label htmlFor="empresa-rep-legal" className="empresa-campo__etiqueta">Representante legal</label>
          <input id="empresa-rep-legal" name="rep_legal" value={form.rep_legal} onChange={handleChange} placeholder="Nombre y apellido" required />
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-rut-representante" className="empresa-campo__etiqueta">RUT representante legal</label>
          <input
            id="empresa-rut-representante"
            name="rut_representante"
            value={form.rut_representante}
            onChange={handleChange}
            placeholder="Ej: 12.345.678-9"
            disabled={form.empresario_individual}
            title={form.empresario_individual ? 'Es el RUT de la empresa y no se puede modificar' : undefined}
            required
          />
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-email" className="empresa-campo__etiqueta">
            Email <span className="empresa-campo__opcional">opcional</span>
          </label>
          <input id="empresa-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="Ej: contacto@empresa.cl" />
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-phone" className="empresa-campo__etiqueta">
            Teléfono <span className="empresa-campo__opcional">opcional</span>
          </label>
          <input
            id="empresa-phone"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="Ej: 56912345678"
            maxLength={11}
          />
          {phoneError && <p className="empresa-campo__error">{phoneError}</p>}
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-clave-sii" className="empresa-campo__etiqueta">Nueva clave SII</label>
          <input
            id="empresa-clave-sii"
            name="clave_sii"
            type="password"
            value={form.clave_sii}
            onChange={handleChange}
            placeholder="Dejar en blanco para no cambiarla"
          />
        </div>
        {/* Oculto temporalmente: aún faltan pruebas. No eliminar.
            El valor guardado se conserva: sigue viajando en el form al guardar. */}
        {/* <select
          name="conciliacion_bancaria"
          value={String(form.conciliacion_bancaria)}
          onChange={(e) => setForm({ ...form, conciliacion_bancaria: e.target.value })}
          required>
          <option value="" disabled>¿Conciliación Bancaria?</option>
          <option value="true">Sí</option>
          <option value="false">No</option>
        </select> */}
        <div className="empresa-campo">
          <label htmlFor="empresa-recursos-humanos" className="empresa-campo__etiqueta">Recursos humanos</label>
          <select
            id="empresa-recursos-humanos"
            name="recursos_humanos"
            value={String(form.recursos_humanos)}
            onChange={(e) => setForm({ ...form, recursos_humanos: e.target.value })}
            required>
            <option value="" disabled>Selecciona una opción</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-area" className="empresa-campo__etiqueta">Tipo de empresa</label>
          <select id="empresa-area" name="area" value={form.area} disabled title="El tipo de empresa no se puede modificar">
            <option value={form.area}>{form.area}</option>
          </select>
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-empresario-individual" className="empresa-campo__etiqueta">Empresario individual con RUT personal</label>
          <select id="empresa-empresario-individual" name="empresario_individual" value={String(form.empresario_individual)} disabled title="No se puede modificar">
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-regimen" className="empresa-campo__etiqueta">Régimen tributario</label>
          <select
            id="empresa-regimen"
            name="regimen_tributario"
            value={form.regimen_tributario}
            onChange={handleChange}
            required>
            <option value="" disabled>Selecciona el régimen tributario</option>
            {REGIMENES_TRIBUTARIOS.map((regimen) => (
              <option key={regimen.codigo} value={regimen.codigo}>{regimen.nombre}</option>
            ))}
          </select>
        </div>
        <div className="empresa-campo">
          <label htmlFor="empresa-tasa-ppm" className="empresa-campo__etiqueta">Tasa PPM</label>
          <div className="empresa-campo__con-sufijo">
            <input
              id="empresa-tasa-ppm"
              name="tasa_ppm"
              type="number"
              step="0.001"
              min="0.001"
              value={form.tasa_ppm}
              onChange={handleChange}
              placeholder="Ej: 0.25"
              required
            />
            <span className="empresa-campo__sufijo">%</span>
          </div>
        </div>
        <div className="empresa-acciones">
          <button type="submit">Guardar cambios</button>
        </div>
      </form>
      <p className="company-settings-hint">
        El <strong>RUT</strong>, el <strong>tipo de empresa</strong> y si es <strong>empresario individual</strong> no se pueden modificar una vez creada la empresa.
      </p>
      <p className="company-settings-hint">
        El cambio de <strong>régimen tributario</strong> aplica a las rentas y declaraciones que se calculen desde ahora; lo ya guardado se mantiene.
      </p>
      <p className="company-settings-hint">
        El cambio de <strong>tasa PPM</strong> aplica a los cierres de mes que se hagan desde ahora.
      </p>
    </div>
  );
};

export default CompanySettingsPage;
